'use client'

import { useCallback, useEffect, useMemo, useState, type BaseSyntheticEvent } from 'react'
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import type { Locale } from '@/root/shared-types'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import {
  firstNameValidationPattern,
  lastNameValidationPattern,
  intlCodeValidationPattern,
  phoneValidationPattern,
} from '@/root/shared-consts/regex-patterns/regexPatterns'
import { updateOwnerInfoAction } from '@/app/actions/reservation-modification'
import { ReservationWithModification } from '../types'
import { handleStaleServerAction } from '../utils'

const customerFormSchema = yup.object({
  firstName: yup.string().trim().required().matches(firstNameValidationPattern),
  lastName: yup.string().trim().required().matches(lastNameValidationPattern),
  intlCode: yup.string().trim().required().matches(intlCodeValidationPattern),
  phoneNumber: yup.string().trim().required().matches(phoneValidationPattern),
  email: yup.string().trim().required().email(),
})

export type CustomerFormValues = yup.InferType<typeof customerFormSchema>

const parsePhone = (
  phone?: { intlCode?: string; phoneNumber?: string } | null
): { intlCode: string; phoneNumber: string } => {
  if (!phone) return { intlCode: '+372', phoneNumber: '' }
  const intlCode = phone.intlCode ? `+${String(phone.intlCode).replace('+', '')}` : '+372'
  return { intlCode, phoneNumber: phone.phoneNumber ?? '' }
}

const toTitleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

interface UseCustomerEditStateParams {
  getTranslation: (key: Key) => string
  locale: Locale
  reservation: ReservationWithModification
  reservationToken: string | undefined
  onDirtyChange?: (dirty: boolean) => void
  onSaved?: (updated: ReservationWithModification) => void
}

export interface UseCustomerEditStateReturn {
  customerEmail: string
  customerName: string
  customerPhone: string
  errors: FieldErrors<CustomerFormValues>
  isDirty: boolean
  isSaving: boolean
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>
  register: UseFormRegister<CustomerFormValues>
  resetForm: () => void
  save: () => Promise<boolean>
  saveError: string | null
}

export function useCustomerEditState({
  reservation,
  reservationToken,
  locale,
  getTranslation,
  onDirtyChange,
  onSaved,
}: UseCustomerEditStateParams): UseCustomerEditStateReturn {
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [savedOwner, setSavedOwner] = useState<ReservationWithModification['reservationOwner'] | null>(null)

  const owner = savedOwner ?? reservation.reservationOwner
  const primaryPhone = owner?.phones?.[0]
  const { intlCode, phoneNumber } = parsePhone(primaryPhone)

  const defaultValues = useMemo<CustomerFormValues>(
    () => ({
      firstName: owner?.firstName ?? '',
      lastName: owner?.lastName ?? '',
      email: owner?.email ?? '',
      intlCode,
      phoneNumber,
    }),
    [owner?.firstName, owner?.lastName, owner?.email, intlCode, phoneNumber]
  )

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CustomerFormValues>({
    mode: 'onChange',
    resolver: yupResolver(customerFormSchema),
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const saveCustomer = useCallback(
    async (values: CustomerFormValues): Promise<boolean> => {
      setSaveError(null)

      const input = {
        token: reservationToken ?? '',
        locale,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        intlCode: values.intlCode,
        phoneNumber: values.phoneNumber,
        companyInfo: owner?.companyInfo ?? null,
        companyId: owner?.companyId,
      }
      setIsSaving(true)
      try {
        const result = await updateOwnerInfoAction(null, input)
        if (result?.success) {
          setSavedOwner(result.reservation.reservationOwner)
          onSaved?.(result.reservation)
          return true
        }

        setSaveError(result.error)
        return false
      } catch (error) {
        if (
          handleStaleServerAction({
            error,
            getTranslation,
            onError: (message) => setSaveError(message),
          })
        ) {
          return false
        }

        setSaveError(getTranslation('editErrorSaveCustomerFailed'))
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [reservationToken, locale, owner?.companyInfo, owner?.companyId, onSaved, getTranslation]
  )

  const onSubmit = handleSubmit(async (values) => {
    await saveCustomer(values)
  })

  const save = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        handleSubmit(
          async (values) => resolve(await saveCustomer(values)),
          () => resolve(false)
        )()
      }),
    [handleSubmit, saveCustomer]
  )

  const resetForm = useCallback(() => {
    reset(defaultValues)
    setSaveError(null)
  }, [reset, defaultValues])

  const customerName = [owner?.firstName, owner?.lastName]
    .filter(Boolean)
    .map((s) => toTitleCase(s!))
    .join(' ')
  const customerPhone = owner?.phones?.[0]
    ? `+${owner.phones[0].intlCode} ${owner.phones[0].phoneNumber}`
    : ''
  const customerEmail = owner?.email ?? ''

  return {
    register,
    errors,
    isDirty,
    isSaving,
    saveError,
    onSubmit,
    save,
    resetForm,
    customerName,
    customerPhone,
    customerEmail,
  }
}
